import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import BaseStorageAdapter from './BaseStorageAdapter';

/**
 * Firebase Storage Adapter
 * Implements storage operations using Firebase Storage
 */
class FirebaseStorageAdapter extends BaseStorageAdapter {
  constructor(firebaseApp) {
    super();
    this.storage = getStorage(firebaseApp);
  }

  /**
   * Upload a file to Firebase Storage
   * @param {File} file - The file to upload
   * @param {string} folder - The folder to upload to
   * @returns {Promise<string>} - The public download URL
   */
  async upload(file, folder) {
    try {
      // Generate unique path
      const filePath = this._generateUniquePath(file, folder);

      // Create a reference to the file location
      const storageRef = ref(this.storage, filePath);

      // Upload the file
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type,
      });

      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log('Firebase Storage: File uploaded successfully:', filePath);
      return downloadURL;
    } catch (error) {
      console.error('Firebase Storage upload error:', error);
      throw new Error(`Failed to upload file to Firebase Storage: ${error.message}`);
    }
  }

  /**
   * Delete a file from Firebase Storage
   * @param {string} url - The download URL of the file to delete
   * @returns {Promise<void>}
   */
  async delete(url) {
    try {
      // Only delete if it's a Firebase Storage URL
      if (!url || !url.includes('firebasestorage.googleapis.com')) {
        console.log('Firebase Storage: Skipping delete - not a Firebase Storage URL');
        return;
      }

      // Extract the file path from the URL
      const filePath = this._extractPathFromURL(url);

      if (!filePath) {
        throw new Error('Could not extract file path from URL');
      }

      // Create a reference to the file
      const storageRef = ref(this.storage, filePath);

      // Delete the file
      await deleteObject(storageRef);

      console.log('Firebase Storage: File deleted successfully:', filePath);
    } catch (error) {
      // If file doesn't exist, that's okay
      if (error.code === 'storage/object-not-found') {
        console.log('Firebase Storage: File not found, already deleted');
        return;
      }

      console.error('Firebase Storage delete error:', error);
      throw new Error(`Failed to delete file from Firebase Storage: ${error.message}`);
    }
  }

  /**
   * Get the public URL for a file path
   * @param {string} path - The file path in storage
   * @returns {Promise<string>} - The public download URL
   */
  async getUrl(path) {
    try {
      const storageRef = ref(this.storage, path);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Firebase Storage getUrl error:', error);
      throw new Error(`Failed to get URL from Firebase Storage: ${error.message}`);
    }
  }

  /**
   * Extract the file path from a Firebase Storage download URL
   * @param {string} url - The download URL
   * @returns {string|null} - The file path or null if extraction fails
   */
  _extractPathFromURL(url) {
    try {
      // Firebase Storage URLs contain the path in the format:
      // https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?...
      const urlObj = new URL(url);
      const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);

      if (pathMatch && pathMatch[1]) {
        // Decode the path (Firebase encodes it)
        return decodeURIComponent(pathMatch[1]);
      }

      return null;
    } catch (error) {
      console.error('Error extracting path from URL:', error);
      return null;
    }
  }
}

export default FirebaseStorageAdapter;
